/**
 * @name AuthStorageService
 * @description Helpers de almacenamiento para DeviceFingerprint y Remember Device
 */

const STORAGE_KEYS = {
  deviceFingerprint: 'ls.deviceFingerprint',
  rememberDeviceToken: 'ls.rememberDeviceToken',
  rememberDeviceExpiresInSeconds: 'ls.rememberDeviceExpiresInSeconds'
};

const safeGetItem = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeSetItem = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const safeRemoveItem = (key) => {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (error) {
    return false;
  }
};

const generateUuid = () => {
  if (
    typeof crypto !== 'undefined' &&
    crypto &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.random() * 16 | 0;
    const value = character === 'x' ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

const getDeviceFingerprint = () => {
  return safeGetItem(STORAGE_KEYS.deviceFingerprint);
};

const getOrCreateDeviceFingerprint = () => {
  let fingerprint = getDeviceFingerprint();

  if (!fingerprint) {
    fingerprint = generateUuid();
    safeSetItem(STORAGE_KEYS.deviceFingerprint, fingerprint);
  }

  return fingerprint;
};

const resetDeviceFingerprint = () => {
  safeRemoveItem(STORAGE_KEYS.deviceFingerprint);
};

const getRememberDeviceToken = () => {
  return safeGetItem(STORAGE_KEYS.rememberDeviceToken);
};

const getRememberDeviceExpiresInSeconds = () => {
  const expiresInSeconds = safeGetItem(STORAGE_KEYS.rememberDeviceExpiresInSeconds);

  if (expiresInSeconds == null || expiresInSeconds === '') {
    return null;
  }

  const parsedValue = Number(expiresInSeconds);
  return Number.isNaN(parsedValue) ? null : parsedValue;
};

const saveRememberDeviceData = ({ rememberDeviceToken, rememberDeviceExpiresInSeconds } = {}) => {
  if (!rememberDeviceToken || typeof rememberDeviceToken !== 'string') {
    return false;
  }

  safeSetItem(STORAGE_KEYS.rememberDeviceToken, rememberDeviceToken);

  if (rememberDeviceExpiresInSeconds != null) {
    safeSetItem(STORAGE_KEYS.rememberDeviceExpiresInSeconds, String(rememberDeviceExpiresInSeconds));
  }

  return true;
};

const clearRememberDeviceToken = () => {
  safeRemoveItem(STORAGE_KEYS.rememberDeviceToken);
  safeRemoveItem(STORAGE_KEYS.rememberDeviceExpiresInSeconds);
};

const clearTrustedDevice = () => {
  clearRememberDeviceToken();
  resetDeviceFingerprint();
};

const buildInteractiveLoginPayload = ({
  userName,
  password,
  clientId,
  keepSession = false,
  rememberDevice = false
}) => {
  const rememberDeviceToken = getRememberDeviceToken();
  const shouldUseFingerprint = Boolean(rememberDevice || rememberDeviceToken);

  return {
    UserName: userName,
    Password: password,
    ClientId: clientId,
    KeepSession: Boolean(keepSession),
    RememberDevice: Boolean(rememberDevice),
    DeviceFingerprint: shouldUseFingerprint ? getOrCreateDeviceFingerprint() : null,
    RememberDeviceToken: rememberDeviceToken || null
  };
};

const buildTwoFactorVerifyPayload = ({
  challengeId,
  code,
  rememberDevice = false
}) => {
  return {
    ChallengeId: challengeId,
    Code: code,
    RememberDevice: Boolean(rememberDevice),
    DeviceFingerprint: rememberDevice ? getOrCreateDeviceFingerprint() : null
  };
};

export const AuthStorageService = {
  STORAGE_KEYS,
  getDeviceFingerprint,
  getOrCreateDeviceFingerprint,
  resetDeviceFingerprint,
  getRememberDeviceToken,
  getRememberDeviceExpiresInSeconds,
  saveRememberDeviceData,
  clearRememberDeviceToken,
  clearTrustedDevice,
  buildInteractiveLoginPayload,
  buildTwoFactorVerifyPayload
};
